import React, { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { InformationCircleIcon } from '@heroicons/react/24/outline'

const TOOLTIP_WIDTH = 256 // px, matches the w-64 the tooltip box renders at

/**
 * A small "ⓘ" affordance that reveals a short explanation on hover/focus,
 * instead of that explanation sitting on the page permanently as its own
 * paragraph. Use this for secondary, "good to know" context (e.g. "editing
 * here doesn't affect candidates who already started") that doesn't need to
 * be read every time the page is — it's the compact alternative to a
 * standing helper-text row.
 *
 * Rendered through a portal into document.body, positioned via the
 * trigger's own getBoundingClientRect() — NOT a plain CSS-absolute child of
 * the trigger. A card this sits inside (e.g. the Interview Configuration
 * card) commonly has `overflow-hidden` to clip its own rounded-corner
 * header background, which was silently cutting this tooltip off the
 * moment it grew past the card's edge. Escaping via a portal sidesteps any
 * ancestor's overflow/z-index entirely, wherever this component is used.
 */
export default function InfoTooltip({
  text,
  align = 'right',
  size = 'sm',
}: {
  text: string
  align?: 'left' | 'right'
  size?: 'sm' | 'md'
}) {
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const buttonSizeClass = size === 'md' ? 'w-6 h-6' : 'w-5 h-5'
  const iconSizeClass = size === 'md' ? 'w-5 h-5' : 'w-4 h-4'

  const show = () => {
    const rect = buttonRef.current?.getBoundingClientRect()
    if (!rect) return
    const rawLeft = align === 'right' ? rect.right - TOOLTIP_WIDTH : rect.left
    // Clamped to stay fully on-screen regardless of where the trigger sits.
    const left = Math.max(8, Math.min(rawLeft, window.innerWidth - TOOLTIP_WIDTH - 8))
    setCoords({ top: rect.bottom + 6, left })
  }
  const hide = () => setCoords(null)

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        tabIndex={0}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={hide}
        className={`${buttonSizeClass} flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-200`}
        aria-label="More info"
      >
        <InformationCircleIcon className={iconSizeClass} />
      </button>
      {coords && createPortal(
        <div
          role="tooltip"
          className="fixed z-[100] rounded-lg bg-gray-900 text-white text-[11px] leading-relaxed px-3 py-2 shadow-lg pointer-events-none"
          style={{ top: coords.top, left: coords.left, width: TOOLTIP_WIDTH }}
        >
          {text}
        </div>,
        document.body
      )}
    </>
  )
}
